// What the factory used, what it cost, and where it went astray.
//
// Mounted from AppMain when activeModule === 'consumptionAnalytics'.
//
// WHY THERE IS NO SINGLE "TOTAL USED" FIGURE
// -------------------------------------------
// Five hundred pairs of gloves plus twenty litres of disinfectant is not
// five hundred and twenty of anything. Quantities are shown per unit, and
// the only figure that spans the whole factory is money — naira being the
// one denominator every item shares. A combined quantity would be
// meaningless, and worse, somebody would put it in a report.
//
// WHY THIN DATA SAYS SO
// ---------------------
// A trend through two points is a straight line. A month-on-month change
// from one record to two is arithmetic wearing a percentage sign. Where
// there is not enough to say something, this screen says that instead of
// drawing a confident chart.
//
// OBSERVATIONS, NOT ACCUSATIONS
// -----------------------------
// High usage might be a busy month, a new product, a training week, a
// leaking bottle, or somebody taking gloves home. The screen reports the
// pattern and names none of those.

import React, { useCallback, useEffect, useState } from 'react';
import { authedFetch } from './utils/api';
import { color, radius, space } from './ui/theme';
import { Banner, Btn, Card, Chip, ErrorBox, SkeletonCards } from './ui/kit';

async function req(url) {
  const res = await authedFetch(url);
  if (!res.ok) {
    let d = `Request failed (${res.status})`;
    try { const j = await res.json(); d = j.detail || d; } catch { /* keep */ }
    throw new Error(typeof d === 'string' ? d : 'Request failed');
  }
  return res.json();
}

async function download(url, fallbackName, notify) {
  try {
    notify?.('Building the file...', 'info');
    const res = await authedFetch(url);
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.detail || `Server returned ${res.status}`);
    }
    const disp = res.headers.get('Content-Disposition') || '';
    const m = disp.match(/filename="?([^"]+)"?/);
    const blob = await res.blob();
    const href = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = m ? m[1] : fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(href);
    notify?.(`Downloaded ${a.download}`, 'success');
  } catch (e) { notify?.(`Download failed: ${e.message}`, 'error'); }
}

const num = (v, dp = 0) =>
  v === null || v === undefined
    ? '—'
    : Number(v).toLocaleString(undefined, { maximumFractionDigits: dp });

const money = (v) =>
  v === null || v === undefined ? '—'
    : 'NGN ' + Number(v).toLocaleString(undefined,
        { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function th(align = 'left') {
  return {
    padding: space(1), textAlign: align, fontWeight: 600,
    borderBottom: `1px solid ${color.border}`, whiteSpace: 'nowrap',
  };
}
function td(align = 'left') {
  return {
    padding: space(1), textAlign: align,
    borderBottom: `1px solid ${color.border}`,
  };
}

// A bar sized against the largest value in its own column. Deliberately not
// a charting library: one number per row, compared with its neighbours, is
// all this needs and a dependency would not make it clearer.
function Bar({ value, max, tone }) {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div style={{
      height: 6, background: color.border, borderRadius: radius.pill,
      overflow: 'hidden', minWidth: 60,
    }}>
      <div style={{
        width: `${pct}%`, height: '100%',
        background: tone || color.medical, borderRadius: radius.pill,
      }} />
    </div>
  );
}

const RANGES = [
  ['7', 'Last 7 days'],
  ['30', 'Last 30 days'],
  ['90', 'Last 90 days'],
];

export default function ConsumptionAnalytics({ notify }) {
  const [days, setDays] = useState('30');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  // The exports use this same string, so a downloaded report always covers
  // exactly the window the screen is showing.
  const windowQuery = (() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - (Number(days) - 1));
    return `start=${start.toISOString().slice(0, 10)}`
         + `&end=${end.toISOString().slice(0, 10)}`;
  })();

  const load = useCallback(async () => {
    setErr('');
    try {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - (Number(days) - 1));
      const q = `start=${start.toISOString().slice(0, 10)}`
              + `&end=${end.toISOString().slice(0, 10)}`;
      const [summary, items, daily, compare, waste, variances] =
        await Promise.all([
          req(`/api/consumption-analytics/summary?${q}`),
          req(`/api/consumption-analytics/by-item?${q}`),
          req(`/api/consumption-analytics/daily?${q}`),
          req(`/api/consumption-analytics/compare?${q}`),
          req(`/api/consumption-analytics/wastage?${q}`),
          req(`/api/consumption-analytics/variances?${q}`),
        ]);
      setData({ summary, items, daily, compare, waste, variances });
    } catch (e) { setErr(e.message); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (err) return <ErrorBox msg={err} />;
  if (!data) return <SkeletonCards n={4} />;

  const { summary, items, daily, compare, waste, variances } = data;
  const maxItemCost = Math.max(...items.map((i) => i.cost), 0);
  const maxDayCost = Math.max(...daily.days.map((d) => d.cost), 0);

  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', gap: space(1),
        marginBottom: space(2), flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', gap: space(1), flexWrap: 'wrap' }}>
          {RANGES.map(([k, label]) => (
            <Btn key={k} size="sm" variant={days === k ? 'primary' : 'secondary'}
                 onClick={() => setDays(k)}>{label}</Btn>
          ))}
        </div>
        <div style={{ display: 'flex', gap: space(1), flexWrap: 'wrap' }}>
          <Btn size="sm" variant="secondary"
               onClick={() => download(
                 `/api/consumption-analytics/download?format=pdf&${windowQuery}`,
                 'consumption-report.pdf', notify)}>
            Download PDF
          </Btn>
          <Btn size="sm" variant="secondary"
               onClick={() => download(
                 `/api/consumption-analytics/download?format=csv&${windowQuery}`,
                 'consumption-report.csv', notify)}>
            CSV
          </Btn>
        </div>
      </div>

      {summary.thin && (
        <div style={{ marginBottom: space(2) }}>
          <Banner tone="info" title="Not much to go on yet">
            {summary.thin_note} Post a few more end-of-day records and the
            patterns below start meaning something.
          </Banner>
        </div>
      )}

      {/* Cost is the only figure that spans every item, so it leads. */}
      <div style={{
        display: 'grid', gap: space(1.5), marginBottom: space(2),
        gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
      }}>
        <Card>
          <div style={{ fontSize: 12.5, color: color.textSecondary }}>
            Consumables cost
          </div>
          <div style={{ fontSize: 24, fontWeight: 650 }}>
            {money(summary.total_cost)}
          </div>
          <div style={{ fontSize: 11.5, color: color.textMuted }}>
            over {summary.days} days
          </div>
        </Card>
        <Card>
          <div style={{ fontSize: 12.5, color: color.textSecondary }}>
            Per operating day
          </div>
          <div style={{ fontSize: 24, fontWeight: 650 }}>
            {summary.cost_per_operating_day === null
              ? '—' : money(summary.cost_per_operating_day)}
          </div>
          <div style={{ fontSize: 11.5, color: color.textMuted }}>
            {summary.posted_records} posted record(s)
          </div>
        </Card>
        <Card>
          <div style={{ fontSize: 12.5, color: color.textSecondary }}>
            Against the period before
          </div>
          <div style={{
            fontSize: 24, fontWeight: 650,
            color: compare.cost_change_pct === null ? color.textMuted
              : compare.cost_change_pct > 0 ? color.danger : color.success,
          }}>
            {compare.cost_change_pct === null ? '—'
              : `${compare.cost_change_pct > 0 ? '+' : ''}${compare.cost_change_pct.toFixed(1)}%`}
          </div>
          <div style={{ fontSize: 11.5, color: color.textMuted }}>
            {compare.comparable ? 'like for like'
              : 'not enough records to compare'}
          </div>
        </Card>
        <Card>
          <div style={{ fontSize: 12.5, color: color.textSecondary }}>
            Unexplained differences
          </div>
          <div style={{
            fontSize: 24, fontWeight: 650,
            color: variances.length > 0 ? color.warning : color.text,
          }}>
            {variances.length}
          </div>
          <div style={{ fontSize: 11.5, color: color.textMuted }}>
            counted vs expected
          </div>
        </Card>
      </div>

      {/* Quantities, per unit, never combined. */}
      {summary.by_unit.length > 0 && (
        <Card>
          <div style={{ fontWeight: 650, fontSize: 14, marginBottom: space(1) }}>
            Used, by unit
          </div>
          <div style={{ fontSize: 12, color: color.textMuted, marginBottom: space(1.5) }}>
            {summary.note}
          </div>
          <div style={{ display: 'flex', gap: space(3), flexWrap: 'wrap' }}>
            {summary.by_unit.map((u) => (
              <div key={u.unit}>
                <div style={{ fontSize: 12.5, color: color.textSecondary }}>
                  {u.unit} · {u.items} item(s)
                </div>
                <div style={{ fontSize: 20, fontWeight: 650 }}>{num(u.consumed, 2)}</div>
                {u.damaged > 0 && (
                  <div style={{ fontSize: 12, color: color.danger }}>
                    + {num(u.damaged, 2)} spoiled
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Per item */}
      <div style={{ marginTop: space(2) }}>
        <Card pad={0}>
          <div style={{ padding: space(1.5), fontWeight: 650, fontSize: 14 }}>
            By item · ranked by cost
            <span style={{ fontWeight: 400, fontSize: 12, color: color.textMuted }}>
              {' '}— the only ranking that means the same for gloves and litres
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: color.bg }}>
                  <th style={th()}>Item</th>
                  <th style={th()}>Unit</th>
                  <th style={th('right')}>Used</th>
                  <th style={th('right')}>Spoiled</th>
                  <th style={th('right')}>Waste</th>
                  <th style={th('right')}>Per day used</th>
                  <th style={th('right')}>Cost</th>
                  <th style={th()} />
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td style={td()} colSpan={8}>
                    Nothing posted in this window.
                  </td></tr>
                )}
                {items.map((i) => (
                  <tr key={i.raw_material_id}>
                    <td style={td()}>{i.name}</td>
                    <td style={{ ...td(), color: color.textSecondary }}>{i.unit}</td>
                    <td style={td('right')}>{num(i.consumed, 2)}</td>
                    <td style={{ ...td('right'), color: i.damaged > 0 ? color.danger : color.textMuted }}>
                      {i.damaged > 0 ? num(i.damaged, 2) : '—'}
                    </td>
                    <td style={{
                      ...td('right'),
                      color: (i.waste_pct || 0) > 5 ? color.danger : color.textSecondary,
                      fontWeight: (i.waste_pct || 0) > 5 ? 600 : 400,
                    }}>
                      {i.waste_pct === null ? '—' : `${i.waste_pct.toFixed(1)}%`}
                    </td>
                    <td style={{ ...td('right'), color: color.textSecondary }}>
                      {num(i.average_per_day_used, 2)}
                    </td>
                    <td style={{ ...td('right'), fontWeight: 600 }}>{money(i.cost)}</td>
                    <td style={{ ...td(), width: 110 }}>
                      <Bar value={i.cost} max={maxItemCost} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Daily */}
      <div style={{ marginTop: space(2) }}>
        <Card>
          <div style={{ fontWeight: 650, fontSize: 14, marginBottom: space(1) }}>
            Cost per day
          </div>
          <div style={{ fontSize: 12, color: color.textMuted, marginBottom: space(1.5) }}>
            {daily.note}
          </div>
          {daily.days.map((d) => (
            <div key={d.date} style={{
              display: 'flex', alignItems: 'center', gap: space(1.5),
              padding: '4px 0',
            }}>
              <div style={{ fontSize: 12.5, color: color.textSecondary, width: 92 }}>
                {d.date}
              </div>
              <div style={{ flex: 1 }}>
                <Bar value={d.cost} max={maxDayCost} />
              </div>
              <div style={{ fontSize: 12.5, width: 130, textAlign: 'right' }}>
                {money(d.cost)}
              </div>
            </div>
          ))}
        </Card>
      </div>

      {/* Wastage and variances, side by side */}
      <div style={{
        marginTop: space(2), display: 'grid', gap: space(2),
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
      }}>
        <Card>
          <div style={{ fontWeight: 650, fontSize: 14, marginBottom: space(1) }}>
            Spoiled rather than used
          </div>
          {waste.length === 0 ? (
            <div style={{ fontSize: 13, color: color.textMuted }}>
              Nothing was written off in this window.
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: color.textMuted, marginBottom: space(1) }}>
                Shown as a share, not a quantity: ten pairs out of ten
                thousand is nothing, ten out of twenty is a storage problem.
              </div>
              {waste.map((w) => (
                <div key={w.raw_material_id} style={{
                  display: 'flex', justifyContent: 'space-between',
                  padding: '4px 0', fontSize: 13,
                }}>
                  <span>{w.name}</span>
                  <span style={{ color: color.danger, fontWeight: 600 }}>
                    {w.waste_pct.toFixed(1)}% · {num(w.damaged, 2)} {w.unit}
                  </span>
                </div>
              ))}
            </>
          )}
        </Card>

        <Card>
          <div style={{ fontWeight: 650, fontSize: 14, marginBottom: space(1) }}>
            Counted differently from the arithmetic
          </div>
          {variances.length === 0 ? (
            <div style={{ fontSize: 13, color: color.textMuted }}>
              Every physical count agreed with the ledger.
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: color.textMuted, marginBottom: space(1) }}>
                Something to look at, not a finding. A one-off is noise; the
                same item repeatedly is worth a question.
              </div>
              {variances.map((v, i) => (
                <div key={i} style={{ padding: '5px 0', fontSize: 13 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: space(1) }}>
                    <span>
                      {v.name}
                      {v.repeated && (
                        <Chip tone="warning">{v.occurrences_in_window}×</Chip>
                      )}
                    </span>
                    <span style={{
                      color: v.variance < 0 ? color.danger : color.warning,
                      fontWeight: 600,
                    }}>
                      {v.variance > 0 ? '+' : ''}{num(v.variance, 2)} {v.unit}
                    </span>
                  </div>
                  <div style={{ fontSize: 11.5, color: color.textMuted }}>
                    {v.record_date} · {v.record_number}
                    {v.reason ? ` · ${v.reason}` : ''}
                  </div>
                </div>
              ))}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
